// User-facing API contracts. Shared between web and api so types stay in sync.

import { z } from 'zod';
import type { TenantRole } from './roles.ts';

// `UserSummary` mirrors the `users` table, which never holds `BesideU_Admin` —
// that role lives in `besideUAdmins` (slice 11, DESIGN §3.2.1). Use `TenantRole`
// so a `UserSummary` consumer can't accidentally widen. `Pending` is the
// post-signup, pre-magic-link-verified status for self-serve HR Admins
// (slice 10, DESIGN §6.6).
export interface UserSummary {
  id: string;
  name: string;
  email: string;
  role: TenantRole;
  status: 'Pending' | 'Active' | 'Opted_Out';
  createdAt: string; // ISO 8601
  // Populated only for role: 'Employee' — the manager from `user_manager_mappings`
  // (single-manager-per-employee, enforced at the write path in
  // `UsersRepository.setManagerMapping`). Null for every other role.
  managerId: string | null;
}

export const USER_STATUSES = ['Pending', 'Active', 'Opted_Out'] as const;

// GET /api/users response shape. The safety-contact fields are org-level
// (not per-row), so they're sibling fields rather than something on each
// `UserSummary` — used to render the Safety Contact indicator + the
// nominate/accept/cancel two-step flow on /hr/members (POST
// /api/users/:id/safety-contact to nominate, /safety-contact/accept,
// /safety-contact/cancel). `total`/`limit`/`offset` describe the full
// filtered result set — `users` is just the current page.
export interface UsersListResponse {
  users: UserSummary[];
  total: number;
  limit: number;
  offset: number;
  hrSafetyContactUserId: string | null;
  pendingHrSafetyContactUserId: string | null;
  pendingHrSafetyContactNominatedAt: string | null; // ISO 8601
}

// HR can invite HR/Manager/Employee roles directly. The canonical
// Employee invite path per DESIGN §6.2 is the magic link dispatched
// at case creation; the Members-page pre-register path is a staging
// carve-out that lets HR create a user row for an Employee before
// a case exists. The /portal "no active case" gate
// (`employee.ts:no_active_case`) renders a friendly empty state
// rather than a broken session for pre-cased Employees.
//
// Pre-staging this list excluded `Employee`. Slice 36 added it so
// the new-case form's `HrNewCase NoEmployeesState` hint ("Invite
// managers and employees from Members before logging a case") is
// actionable for the Employee half. The trade-off: a pre-cased
// Employee lands in /portal with no case context. The HrNewCase
// form is the long-term goal (inline create on case) — until that
// lands, this list is the working surface.
export const INVITABLE_ROLES = [
  'HR_Admin',
  'HR_Viewer',
  'Manager',
  'Employee',
] as const satisfies readonly TenantRole[];

// GET /api/users query params — search + filter + pagination. `role` reuses
// `INVITABLE_ROLES` (the exact `TenantRole` set that can appear in `users`)
// rather than a second literal list that could drift from it.
export const ListUsersRequestSchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(USER_STATUSES).optional(),
  role: z.enum(INVITABLE_ROLES).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
});
export type ListUsersRequest = z.infer<typeof ListUsersRequestSchema>;

// GET /api/users/roster query — an unpaginated, ALL-statuses fetch of every
// user of a single role. Deliberately excludes `Employee`: HR/Manager
// headcount is bounded even when total org headcount (Employees) isn't, so
// restricting the allowed roles is what keeps this safe to leave unpaginated
// (mirrors the existing unpaginated `listByRole('Employee')` case-create-form
// picker only in that it's role-scoped — that one stays Active-only and
// paginated-adjacent by construction; this one exists so pickers can resolve
// a currently-assigned-but-now-inactive person's name, e.g. a deactivated
// Manager still referenced by an Employee's `user_manager_mappings` row).
export const ROSTER_ROLES = [
  'HR_Admin',
  'HR_Viewer',
  'Manager',
] as const satisfies readonly TenantRole[];
export const RosterRequestSchema = z.object({
  role: z.enum(ROSTER_ROLES),
});
export type RosterRequest = z.infer<typeof RosterRequestSchema>;

export interface RosterResponse {
  users: UserSummary[];
}

export const InviteUserRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
  role: z.enum(INVITABLE_ROLES),
});
export type InviteUserRequest = z.infer<typeof InviteUserRequestSchema>;

// Sets (replaces) the manager mapping for an Employee — PUT /api/users/:id/manager.
export const SetManagerRequestSchema = z.object({
  managerId: z.string().uuid(),
});
export type SetManagerRequest = z.infer<typeof SetManagerRequestSchema>;

// Changes an existing member's role — POST /api/users/:id/role. Reuses
// `INVITABLE_ROLES` for the same reason `InviteUserRequestSchema` does: the
// exact set of roles HR may assign a tenant user, in one place.
export const SetRoleRequestSchema = z.object({
  role: z.enum(INVITABLE_ROLES),
});
export type SetRoleRequest = z.infer<typeof SetRoleRequestSchema>;
